import EmptyState from '@/components/empty-state';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import ValidationErrors from '@/components/validation-errors';
import type { Todo } from '@/types/todo';
import {
  project_todo_completion_path,
  project_todo_path,
  project_todos_path,
} from '@/rails/routes';
import { Form, router } from '@inertiajs/react';
import { Plus } from 'lucide-react';
import { useState } from 'react';

interface Props {
  projectId: string;
  todos: Todo[];
}

export default function TodosCard({ projectId, todos }: Props) {
  const [adding, setAdding] = useState(false);
  const openCount = todos.filter((todo) => !todo.completed).length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base font-medium">
          Todos
          {todos.length > 0 ? (
            <span className="text-muted-foreground ml-2 text-sm font-normal">
              {openCount} open
            </span>
          ) : null}
        </CardTitle>
        {!adding ? (
          <Button type="button" size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="size-3.5" />
            Add todo
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {adding ? (
          <Form
            method="post"
            action={project_todos_path(projectId)}
            className="bg-muted/40 space-y-3 rounded-lg border p-3"
            onSuccess={() => setAdding(false)}
          >
            {({ errors, processing }) => (
              <>
                <Input
                  id="todo_title"
                  name="todo[title]"
                  placeholder="What needs doing?"
                  required
                  autoFocus
                />
                <ValidationErrors errors={errors.title} />
                <div className="flex gap-2">
                  <Button type="submit" size="sm" disabled={processing}>
                    Add
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setAdding(false)}
                    disabled={processing}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </Form>
        ) : null}

        {todos.length === 0 && !adding ? (
          <EmptyState description="No todos yet." className="py-2" />
        ) : null}

        {todos.length > 0 ? (
          <ul className="divide-y">
            {todos.map((todo) => (
              <li key={todo.id} className="flex items-center justify-between gap-3 py-2.5">
                <label className="flex min-w-0 flex-1 items-center gap-3">
                  <Checkbox
                    checked={todo.completed}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        router.post(project_todo_completion_path(projectId, todo.id));
                      } else {
                        router.delete(project_todo_completion_path(projectId, todo.id));
                      }
                    }}
                  />
                  <span
                    className={
                      todo.completed ? 'text-muted-foreground line-through' : undefined
                    }
                  >
                    {todo.title}
                  </span>
                </label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-muted-foreground"
                  onClick={() => router.delete(project_todo_path(projectId, todo.id))}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  );
}
